import React from 'react';
import { Box } from '@amzn/awsui-components-core';
import { LifecycleHealthMetricModalConfig } from './UserMetricsInterfaces';

export const WITHOUT_USERS_COLUMN_LIST = [
  'log_month',
  'application',
  'application_type',
  'team_name',
  'supervisor_login_name_hierarchy',
  'supervisor_name_hierarchy'
];

export const WITHOUT_USERS_AGGREGATE_COLUMN_LISTS = [
  { name: 'measure_value', operation: 'sum' },
  { name: 'distinct_users', operation: 'count' }
];

export const APPLICATION_FOOTPRINT_COLUMN_LISTS = ['application_type', 'team_name'];

export const APPLICATION_USER_VIEW_COLUMN_LISTS = [...APPLICATION_FOOTPRINT_COLUMN_LISTS, 'user_login'];

export const APPLICATION_USER_VIEW_MEASURE_COLUMN_LISTS = [
  { name: 'measure_value', operation: 'sum' },
  { name: 'active_since', operation: 'minDate' },
  { name: 'last_active', operation: 'maxDate' }
];

export const SUB_APPLICATION_FOOTPRINT_COLUMN_LISTS = [...APPLICATION_FOOTPRINT_COLUMN_LISTS, 'application'];

export const SUB_APPLICATION_FOOTPRINT_MEASURE_COLUMN_LISTS = [
  { name: 'measure_value', operation: 'sum' },
  { name: 'active_since', operation: 'minDate' },
  { name: 'user_login', operation: 'countDistinct' }
];

export const APPLICATION_FOOTPRINT_MEASURE_COLUMN_LISTS = [...SUB_APPLICATION_FOOTPRINT_MEASURE_COLUMN_LISTS];

export const SUB_APPLICATION_USER_VIEW_COLUMN_LISTS = [...SUB_APPLICATION_FOOTPRINT_COLUMN_LISTS, 'user_login', 'active_since', 'last_active'];

export const SUB_APPLICATION_USER_VIEW_MEASURE_COLUMN_LISTS = [{ name: 'measure_value', operation: 'sum' }];

export const APPLICATION = 'application';

export const USER_METRICS_DASHBOARD_COLUMN_LIST = ['application_type', 'log_month'];

export const USER_METRICS_DASHBOARD_MEASURE_COLUMN_LIST = [
  { name: 'user_login', operation: 'countDistinct' },
  { name: 'measure_value', operation: 'sum' }
];

export const USER_METRICS_TOP_5_APPLICATION_BY_MONTH_COLUMN_LIST = ['application', 'log_month'];

export const USER_METRICS_TOP_5_APPLICATION_BY_MONTH_MEASURE_COLUMN_LIST = [{ name: 'user_login', operation: 'countDistinct' }];

export const USER_METRICS_TOTAL_UNIQUE_USERS_BY_MONTH_COLUMN_LIST = ['log_month', 'application_type'];

export const USER_METRICS_TOTAL_UNIQUE_USERS_BY_MONTH_MEASURE_COLUMN_LIST = [...USER_METRICS_TOP_5_APPLICATION_BY_MONTH_MEASURE_COLUMN_LIST];

export const USER_METRICS_AGG_APPLICATION_BY_USERS_COLUMN_LIST = ['application_type', 'create_date_formatted'];

export const USER_METRICS_AGG_SUB_APPLICATION_COLUMN_LIST = ['application', ...USER_METRICS_AGG_APPLICATION_BY_USERS_COLUMN_LIST];

export const USER_METRICS_AGG_APPLICATION_BY_USERS_MEASURE_COLUMN_LIST = [...USER_METRICS_TOP_5_APPLICATION_BY_MONTH_MEASURE_COLUMN_LIST];

export const DAS_FINTECH_ORG_MANAGER = 'Michael DeGard';

export const DAS_FINTECH_ORG_MANAGER_ALIAS = 'degardm';

// Target job levels for manager aggregation (7, 8, 9, 10 - level 9 included for future-proofing)
export const MANAGER_TARGET_JOB_LEVELS = [7, 8, 9, 10, 11, 12];

// ===== Hierarchy Metrics Tab Constants =====

// View mode options for hierarchy display
export const HIERARCHY_VIEW_MODE = {
  MANAGER: 'manager_view',
  APPLICATION: 'app_view',
  JOB_FAMILY: 'job_family_view'
} as const;

// Lifecycle health metrics thresholds
export const ENGAGED_USER_THRESHOLD = 10; // Users with > this many views are "engaged"
export const POWER_USER_PERCENTILE = 0.1; // Top 10% of users by activity

// Job Family "Others" grouping constants
export const JOB_FAMILY_OTHERS_PATH_KEY = 'Others';
export const JOB_FAMILY_OTHERS_THRESHOLD = 0.02;
export const JOB_FAMILY_OTHERS_LABEL = `Other Job Families (≤${JOB_FAMILY_OTHERS_THRESHOLD * 100}% users)`;
// View mode options for application display in hierarchy display
export const APPLICATION_HIERARCHY_VIEW_MODE = {
  APPLICATION: 'application',
  SUB_APPLICATION: 'sub_application'
};

export type HierarchyViewMode = (typeof HIERARCHY_VIEW_MODE)[keyof typeof HIERARCHY_VIEW_MODE];
export type ApplicationViewMode = (typeof APPLICATION_HIERARCHY_VIEW_MODE)[keyof typeof APPLICATION_HIERARCHY_VIEW_MODE];

// Default manager level filter
export const DEFAULT_MANAGER_LEVEL = 11;

// UI dimensions
export const HIERARCHY_GRID_HEIGHT = '60vh';
export const DETAIL_PANEL_HEIGHT = '519px';

// Base column definitions for User Details grid (shared between Application and Sub Application views)
// Note: cellRenderer for user_login and valueFormatter/valueGetter must be added in component since they use React/JSX
export const USER_DETAILS_BASE_COLUMNS = [
  { field: 'application_type', headerName: 'Application', flex: 1 },
  { field: 'user_login', headerName: 'User Alias', flex: 1 },
  { field: 'team_name', headerName: 'Team', flex: 1 },
  { field: 'active_since', headerName: 'Active Since', flex: 1 },
  { field: 'last_active', headerName: 'Last Accessed', flex: 1 },
  { field: 'measure_value', headerName: 'View Count', flex: 1 },
  { field: 'manager_name_hierarchy_concat', headerName: 'Leadership Hierarchy', flex: 2 }
];

// Sub Application column (prepended in Sub Application view)
export const SUB_APPLICATION_COLUMN = { field: 'application', headerName: 'Sub Application', flex: 1 };

// ===== Hierarchy Grid Configuration Constants =====

// Default column definition for hierarchy grid
export const HIERARCHY_DEFAULT_COL_DEF = {
  flex: 1,
  minWidth: 100,
  sortable: true,
  filter: true,
  resizable: true
};

// Locale text for AG-Grid
export const HIERARCHY_LOCALE_TEXT = {
  rowGroupColumnsEmptyMessage: 'Drag here for grouping'
};

// Grid container style
export const HIERARCHY_GRID_STYLE = {
  height: HIERARCHY_GRID_HEIGHT,
  width: '100%'
};

// ===== Export Processing Utility =====

/**
 * Processes cell values for Excel export from AG-Grid.
 * Handles arrays, objects, null values, and skips the details column.
 */
export const processExcelCellValue = (cellParams: any): string => {
  // Skip the details column by returning empty string
  if (cellParams.column.getColId() === 'details') {
    return '';
  }
  // For the auto-group column, return the full path for proper hierarchy display in Excel
  if (cellParams.column.getColId() === 'ag-Grid-AutoColumn') {
    if (cellParams.node?.data?.path && Array.isArray(cellParams.node.data.path)) {
      return cellParams.node.data.path.join(' > ');
    }
  }
  // Handle null/undefined
  if (cellParams.value === null || cellParams.value === undefined) {
    return '';
  }
  // Convert arrays to strings (for path data)
  if (Array.isArray(cellParams.value)) {
    return cellParams.value.join(' > ');
  }
  // Convert objects to strings
  if (typeof cellParams.value === 'object') {
    return String(cellParams.value);
  }
  // Convert numbers to strings for Excel export
  if (typeof cellParams.value === 'number') {
    return String(cellParams.value);
  }
  return cellParams.value;
};

export const DAS_FIN_SUITE_WEBSITE = 'DasFinSuite-website';

// ===== Lifecycle Health Metrics Info (for info modal) =====

export const LIFECYCLE_HEALTH_METRIC_INFO: LifecycleHealthMetricModalConfig = {
  title: "Lifecycle Health Metrics — How They're Calculated",
  description: React.createElement(
    React.Fragment,
    null,
    'All metrics are calculated per application (or sub-application) and team combination. Use the ',
    React.createElement(Box, { variant: 'strong' } as any, 'Basis Month'),
    ' dropdown to select which month to analyze from, and the ',
    React.createElement(Box, { variant: 'strong' } as any, 'Basis Term'),
    ' to control the comparison window size (1 month, 2 months for bimonthly, or 3 months for quarterly). A 2-month term means the basis period spans 2 consecutive months, and comparisons are made against the preceding 2-month period.'
  ),
  sections: [
    {
      sectionTitle: 'User Classification (Base Counts)',
      metrics: [
        {
          metric: 'Distinct Users',
          formula: '',
          description:
            'Total unique users recorded in the selected basis period. Equals New + Returning + Retained users. With a multi-month term, this is the union of unique users across all months in the basis period.'
        },
        {
          metric: 'New',
          formula: '',
          description: 'Users active in the basis period but not in the prior period or any older period within the data window.'
        },
        {
          metric: 'Retained',
          formula: '',
          description: 'Users active in both the basis period AND the prior period (period-over-period continuity).'
        },
        {
          metric: 'Returning',
          formula: '',
          description:
            'Users active in the basis period and in an older period (beyond the prior period), but not in the prior period (re-engaged after a gap).'
        },
        {
          metric: 'Lost',
          formula: '',
          description: 'Users who were active in the prior period but not in the basis period.'
        },
        {
          metric: 'Prior Period Users',
          formula: '',
          description:
            'Total unique users in the prior period. With a multi-month term, this is the union of unique users across all months in the prior period (immediately preceding the basis period).'
        }
      ]
    },
    {
      sectionTitle: 'Core Conversion Percentages',
      metrics: [
        { metric: 'New User %', formula: 'New Users ÷ Distinct Users', description: "What share of the basis period's users are brand new?" },
        { metric: 'Retained %', formula: 'Retained Users ÷ Prior Period Users', description: "Of the prior period's users, how many came back?" },
        { metric: 'Returning %', formula: 'Returning Users ÷ Distinct Users', description: 'What share are re-engaged after a gap?' },
        { metric: 'Lost %', formula: 'Lost Users ÷ Prior Period Users', description: 'Of prior period users, how many dropped off?' }
      ]
    },
    {
      sectionTitle: 'Funnel / Lifecycle Rates',
      metrics: [
        {
          metric: 'Activation Rate',
          formula: '(New + Returning) ÷ Distinct Users',
          description: 'Share of users who are newly active or re-engaged (vs. retained).'
        },
        {
          metric: 'Continuity Rate',
          formula: 'Retained ÷ Distinct Users',
          description: 'Share of users who were in the prior period and are still active in the basis period.'
        },
        {
          metric: 'Reactivation Rate',
          formula: 'Returning ÷ (Returning + Lost)',
          description: 'Of users who were inactive in the prior period, how many came back vs. stayed gone?'
        }
      ]
    },
    {
      sectionTitle: 'Health / Quality Signals',
      metrics: [
        {
          metric: 'Net Growth %',
          formula: '(New + Returning − Lost) ÷ Prior Period Users',
          description: 'Is the user base growing or shrinking? Positive = growth.'
        },
        {
          metric: 'Retention Ratio',
          formula: 'Retained ÷ Lost',
          description: "Ratio of retained users to lost users. Higher is better (above 1.0 means you're keeping more than you're losing)."
        },
        {
          metric: 'Engaged User %',
          formula: 'Users with > 10 views ÷ Distinct Users',
          description: 'Proportion of users who are meaningfully engaged (not just a single visit).'
        },
        {
          metric: 'Activity/User',
          formula: 'Basis Period Views ÷ Distinct Users',
          description: 'Average number of page views per user in the basis period.'
        },
        {
          metric: 'Power User %',
          formula: 'Users exceeding global top-10% view threshold ÷ Distinct Users',
          description:
            'Users whose activity meets or exceeds the global top-10% view-count cutoff (computed across all groups). Apps with heavier usage will show a higher Power User %.'
        }
      ]
    }
  ]
};
